// src/utils/apiCache.js
// ============================================
// IN-MEMORY CACHE - Prevents redundant API calls
// ============================================
import { getTenantId } from '../config/tenantConfig';

const CACHE_TTL = 10 * 60 * 1000; // 10 minutes

const cache = {
    tenantId: null,              // which tenant this cache's data belongs to
    categories: null,
    categoriesTimestamp: 0,
    storeDetails: {},           // keyed by store ID
    storeDetailsTimestamp: {},   // keyed by store ID
    branches: null,
    branchesTimestamp: 0,
    popularItems: {},           // keyed by branch ID
    popularItemsTimestamp: {},  // keyed by branch ID
    menuData: {},               // keyed by branch ID
    menuDataTimestamp: {},      // keyed by branch ID
    // Separate from menuData above: MenuSection (home page "Our Menu"
    // preview) stores a small, differently-shaped payload here (a handful
    // of categories each holding a handful of simplified items) — it used
    // to share the menuData key with RestaurantMenuApp's full flat product
    // list, so whichever of the two fetched first would silently leave the
    // other consuming its incompatible data on a "cache hit".
    menuPreviewData: {},        // keyed by branch ID
    menuPreviewDataTimestamp: {}, // keyed by branch ID
    orders: null,
    ordersTimestamp: 0,
    transactions: null,
    transactionsTimestamp: 0,
    userProfile: null,
    userProfileTimestamp: 0,
};

// This cache is a module-level singleton with no natural per-tenant scoping,
// so a branch/store cached while browsing one tenant's storefront would
// otherwise silently leak into another tenant's session in the same browser
// origin (e.g. QA testers switching between tenants). Every getter below
// calls this first — if the resolved tenant has changed since the cache was
// last populated, wipe everything before returning anything from it.
function ensureTenantScope() {
    const currentTenantId = getTenantId();
    if (currentTenantId !== cache.tenantId) {
        resetCache();
        cache.tenantId = currentTenantId;
    }
}

function resetCache() {
    cache.categories = null;
    cache.categoriesTimestamp = 0;
    cache.storeDetails = {};
    cache.storeDetailsTimestamp = {};
    cache.branches = null;
    cache.branchesTimestamp = 0;
    cache.popularItems = {};
    cache.popularItemsTimestamp = {};
    cache.menuData = {};
    cache.menuDataTimestamp = {};
    cache.menuPreviewData = {};
    cache.menuPreviewDataTimestamp = {};
    cache.orders = null;
    cache.ordersTimestamp = 0;
    cache.transactions = null;
    cache.transactionsTimestamp = 0;
    cache.userProfile = null;
    cache.userProfileTimestamp = 0;
}

export const apiCache = {
    // ── Categories ──
    getCategories() {
        ensureTenantScope();
        if (cache.categories && (Date.now() - cache.categoriesTimestamp) < CACHE_TTL) {
            console.log('⚡ Cache HIT: categories');
            return cache.categories;
        }
        return null;
    },

    setCategories(data) {
        cache.categories = data;
        cache.categoriesTimestamp = Date.now();
    },

    // ── Store Details ──
    getStoreDetails(storeId) {
        ensureTenantScope();
        const key = String(storeId);
        if (
            cache.storeDetails[key] &&
            (Date.now() - (cache.storeDetailsTimestamp[key] || 0)) < CACHE_TTL
        ) {
            console.log(`⚡ Cache HIT: storeDetails[${key}]`);
            return cache.storeDetails[key];
        }
        return null;
    },

    setStoreDetails(storeId, data) {
        const key = String(storeId);
        cache.storeDetails[key] = data;
        cache.storeDetailsTimestamp[key] = Date.now();
    },

    // ── Branches ──
    getBranches() {
        ensureTenantScope();
        if (cache.branches && (Date.now() - cache.branchesTimestamp) < CACHE_TTL) {
            console.log('⚡ Cache HIT: branches');
            return cache.branches;
        }
        return null;
    },

    setBranches(data) {
        cache.branches = data;
        cache.branchesTimestamp = Date.now();
    },

    // ── Popular Items ──
    getPopularItems(branchId) {
        ensureTenantScope();
        const key = String(branchId || 'any');
        if (
            cache.popularItems[key] &&
            (Date.now() - (cache.popularItemsTimestamp[key] || 0)) < CACHE_TTL
        ) {
            console.log(`⚡ Cache HIT: popularItems[${key}]`);
            return cache.popularItems[key];
        }
        return null;
    },

    setPopularItems(branchId, data) {
        const key = String(branchId || 'any');
        cache.popularItems[key] = data;
        cache.popularItemsTimestamp[key] = Date.now();
    },

    // ── Menu Data ──
    getMenuData(branchId) {
        ensureTenantScope();
        const key = String(branchId || 'any');
        if (
            cache.menuData[key] &&
            (Date.now() - (cache.menuDataTimestamp[key] || 0)) < CACHE_TTL
        ) {
            console.log(`⚡ Cache HIT: menuData[${key}]`);
            return cache.menuData[key];
        }
        return null;
    },

    setMenuData(branchId, data) {
        const key = String(branchId || 'any');
        cache.menuData[key] = data;
        cache.menuDataTimestamp[key] = Date.now();
    },

    // ── Menu Preview Data (MenuSection's home-page "Our Menu" preview —
    //    kept separate from getMenuData/setMenuData above; see comment on
    //    cache.menuPreviewData for why) ──
    getMenuPreviewData(branchId) {
        ensureTenantScope();
        const key = String(branchId || 'any');
        if (
            cache.menuPreviewData[key] &&
            (Date.now() - (cache.menuPreviewDataTimestamp[key] || 0)) < CACHE_TTL
        ) {
            console.log(`⚡ Cache HIT: menuPreviewData[${key}]`);
            return cache.menuPreviewData[key];
        }
        return null;
    },

    setMenuPreviewData(branchId, data) {
        const key = String(branchId || 'any');
        cache.menuPreviewData[key] = data;
        cache.menuPreviewDataTimestamp[key] = Date.now();
    },

    // ── Orders ──
    getOrders() {
        ensureTenantScope();
        if (cache.orders && (Date.now() - cache.ordersTimestamp) < CACHE_TTL) {
            console.log('⚡ Cache HIT: orders');
            return cache.orders;
        }
        return null;
    },

    setOrders(data) {
        cache.orders = data;
        cache.ordersTimestamp = Date.now();
    },

    // ── Transactions ──
    getTransactions() {
        ensureTenantScope();
        if (cache.transactions && (Date.now() - cache.transactionsTimestamp) < CACHE_TTL) {
            console.log('⚡ Cache HIT: transactions');
            return cache.transactions;
        }
        return null;
    },

    setTransactions(data) {
        cache.transactions = data;
        cache.transactionsTimestamp = Date.now();
    },

    // ── User Profile ──
    getUserProfile() {
        ensureTenantScope();
        if (cache.userProfile && (Date.now() - cache.userProfileTimestamp) < CACHE_TTL) {
            console.log('⚡ Cache HIT: userProfile');
            return cache.userProfile;
        }
        return null;
    },

    setUserProfile(data) {
        cache.userProfile = data;
        cache.userProfileTimestamp = Date.now();
    },

    // ── Helpers ──
    invalidateCategories() {
        cache.categories = null;
        cache.categoriesTimestamp = 0;
    },

    invalidateUserProfile() {
        cache.userProfile = null;
        cache.userProfileTimestamp = 0;
    },

    invalidateStoreDetails(storeId) {
        if (storeId) {
            const key = String(storeId);
            delete cache.storeDetails[key];
            delete cache.storeDetailsTimestamp[key];
        } else {
            cache.storeDetails = {};
            cache.storeDetailsTimestamp = {};
        }
    },

    invalidateAll() {
        resetCache();
        cache.tenantId = null;
        console.log('🗑️ API cache cleared');
    },
};

export default apiCache;
